// src/components/UserProfile.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import SocialLinksForm from './SocialLinksForm';
import SocialLinksDisplay from './SocialLinksDisplay';
import OnboardingModal from './OnboardingModal';

import { auth, db, storage, functions } from '../firebase';
import CONFIG from '../config';

import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { 
  Check, 
  X, 
  Link as LinkIcon, 
  ShieldCheck, 
  CheckCircle2,
  Lock,
  Pencil,
  Copy,
  RefreshCw,
  User,
  CreditCard,
  LogOut,
  Zap
} from 'lucide-react';

// UI Components
import { Button } from './ui/Button';
import { Card, CardGrid } from './ui/Card';
import { Input, TextArea } from './ui/Input';
import { Tabs } from './ui/Tabs';
import { Alert } from './ui/Alert';

function UserProfile() {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [socialLinks, setSocialLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [newFullName, setNewFullName] = useState('');
  const [newBio, setNewBio] = useState('');
  const [newProfilePicFile, setNewProfilePicFile] = useState(null);
  const [isUploadingPic, setIsUploadingPic] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);

  const handleProfilePicUpload = async (file) => {
    if (!file || !currentUser) return;
    
    setIsUploadingPic(true);
    try {
      const timestamp = Date.now();
      const fileRef = storageRef(storage, `profilePics/${currentUser.uid}/${timestamp}`);
      await uploadBytesResumable(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, { profilePic: downloadURL });
      
      setUserData(prev => ({ ...prev, profilePic: downloadURL }));
      toast.success('Profile picture updated!');
    } catch (error) {
      console.error('Error uploading pic:', error);
      toast.error('Failed to upload profile picture.');
    } finally {
      setIsUploadingPic(false);
    }
  };
  
  // Tab state
  const [activeTab, setActiveTab] = useState('profile');
  const billingRef = useRef(null);

  // Vanity URL state
  const [vanityId, setVanityId] = useState('');
  const [isCheckingVanity, setIsCheckingVanity] = useState(false);
  const [vanityAvailable, setVanityAvailable] = useState(null);
  const [vanityError, setVanityError] = useState('');
  const [isSavingVanity, setIsSavingVanity] = useState(false);
  const [isEditingVanity, setIsEditingVanity] = useState(false);

  // Upgrade state
  const [upgradingPlan, setUpgradingPlan] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const fetchUserData = async (userId) => {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userSnapshot = await getDoc(userDocRef);
      if (userSnapshot.exists()) {
        const data = userSnapshot.data();
        setUserData(data);
        setNewFullName(data.fullName || '');
        setNewBio(data.bio || '');
        setVanityId(data.vanityId || '');
        setSocialLinks(data.socialLinks || []);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      toast.error('Error logging out. Please try again.');
    }
  };

  const handleUpgrade = async (tierKey) => {
    const plan = CONFIG.tiers[tierKey];
    if (!plan?.stripePriceId || !currentUser) return;
    
    setUpgradingPlan(tierKey);
    try {
      // 1. Get Authentication Token
      const idToken = await currentUser.getIdToken();
      
      // 2. Call the Express Endpoint
      const response = await fetch('https://us-central1-reelintro-cb609.cloudfunctions.net/createCheckoutSession', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          priceId: plan.stripePriceId,
          successUrl: `${window.location.origin}/profile?from=purchase`,
          cancelUrl: window.location.href,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create session');
      }

      const { sessionId } = await response.json();
      
      // 3. Redirect to Stripe
      if (sessionId) {
        const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
        if (stripe) {
          await stripe.redirectToCheckout({ sessionId });
        } else {
          throw new Error('Could not load Stripe library.');
        }
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error(error.message || 'Failed to start checkout.');
    } finally {
      setUpgradingPlan(null);
    }
  };

  const checkAvailability = async (id) => {
    if (id.length < 3) {
      setVanityAvailable(null);
      return;
    }
    setIsCheckingVanity(true);
    try {
      const checkFn = httpsCallable(functions, 'checkVanityIdAvailability');
      const result = await checkFn({ vanityId: id });
      setVanityAvailable(result.data.available);
      setVanityError(result.data.reason || '');
    } catch (error) {
      console.error('Error checking vanity:', error);
    } finally {
      setIsCheckingVanity(false);
    }
  };

  const handleClaimVanity = async () => {
    if (!vanityAvailable || isSavingVanity) return;
    
    if (userData.vanityId) {
      const confirm = window.confirm("⚠️ WARNING: Changing your link will BREAK any existing links you've shared (on LinkedIn, resumes, etc.). Are you sure you want to proceed?");
      if (!confirm) return;
    }

    setIsSavingVanity(true);
    try {
      const claimFn = httpsCallable(functions, 'claimVanityId');
      await claimFn({ vanityId });
      toast.success('Vanity URL updated successfully!');
      setIsEditingVanity(false);
      fetchUserData(currentUser.uid);
    } catch (error) {
      toast.error(error.message || 'Failed to update vanity ID.');
    } finally {
      setIsSavingVanity(false);
    }
  };

  useEffect(() => {
    if (!vanityId || (userData && userData.vanityId === vanityId)) {
      setVanityAvailable(null);
      setVanityError('');
      return;
    }
    const timer = setTimeout(() => checkAvailability(vanityId), 500);
    return () => clearTimeout(timer);
  }, [vanityId, userData]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
        fetchUserData(user.uid);
        if (location.search.includes('upgrade=true')) {
          setActiveTab('billing');
          setTimeout(() => billingRef.current?.scrollIntoView({ behavior: 'smooth' }), 500);
        }
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate, location]);

  const handleSaveProfile = async () => {
    if (!currentUser) return;
    const userDocRef = doc(db, 'users', currentUser.uid);
    let updatedData = { 
      fullName: newFullName,
      bio: newBio, 
      socialLinks: socialLinks.map(link => ({...link, url: link.url.startsWith('http') ? link.url : `https://${link.url}`}))
    };

    try {
      await updateDoc(userDocRef, updatedData);
      toast.success('Profile updated!');
      fetchUserData(currentUser.uid);
      setIsEditing(false);
    } catch (error) {
      toast.error('Error updating profile.');
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><ClipLoader size={50} color="#2563eb" /></div>;

  const currentTier = userData?.subscriptionTier || 'none';
  const publicProfileUrl = vanityId ? `${window.location.origin}/${vanityId}` : null;

  const profileTabs = [
    { id: 'profile', label: 'Profile Settings', icon: User },
    { id: 'billing', label: 'Subscription & Billing', icon: CreditCard },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
      <OnboardingModal isOpen={showOnboarding} onClose={() => setShowOnboarding(false)} initialStep={onboardingStep} />

      <Tabs tabs={profileTabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'profile' ? (
        <div className="space-y-8 animate-in fade-in duration-300">
          <Card padding="p-0">
            <div className="h-32 bg-gradient-to-r from-blue-600 to-indigo-700"></div>
            <div className="px-8 pb-8">
              <div className="relative flex flex-col md:flex-row items-center md:items-end -mt-12 mb-6">
                <div className="relative group">
                  <div className="w-32 h-32 rounded-full border-4 border-white overflow-hidden shadow-lg bg-gray-100 relative">
                    <img 
                      src={userData.profilePic || '/default-thumbnail.jpg'} 
                      alt="Profile" 
                      className={`w-full h-full object-cover transition-opacity ${isUploadingPic ? 'opacity-30' : 'opacity-100'}`} 
                    />
                    {isUploadingPic && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <ClipLoader size={24} color="#2563eb" />
                      </div>
                    )}
                    <label className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                      <Pencil className="text-white w-6 h-6" />
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={(e) => handleProfilePicUpload(e.target.files[0])} 
                      />
                    </label>
                  </div>
                </div>
                <div className="md:ml-6 mt-4 md:mb-2 text-center md:text-left flex-grow">
                  <h1 className="text-2xl font-bold text-gray-900">{userData.fullName || 'New User'}</h1>
                  <p className="text-gray-500">{currentUser.email}</p>
                </div>
                {!isEditing ? (
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} icon={Pencil} className="md:mb-2">
                    Edit Profile
                  </Button>
                ) : (
                  <div className="flex space-x-2 md:mb-2">
                    <Button size="sm" onClick={handleSaveProfile}>Save Changes</Button>
                    <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>Cancel</Button>
                  </div>
                )}
              </div>
              {isEditing ? (
                <div className="space-y-4 max-w-2xl">
                  <Input label="Full Name" value={newFullName} onChange={(e) => setNewFullName(e.target.value)} placeholder="Full Name" />
                  <div className="relative">
                    <TextArea 
                      label="Bio" 
                      value={newBio} 
                      onChange={(e) => setNewBio(e.target.value.slice(0, 500))} 
                      rows={4} 
                      placeholder="Share a brief professional summary (max 500 characters)..." 
                      helperText={`${newBio.length}/500 characters. Aim for a short, high-impact intro.`}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-gray-700 max-w-3xl leading-relaxed bg-gray-50/50 p-4 rounded-lg border border-gray-100 italic">
                  "{userData.bio || 'Your bio will appear here.'}"
                </p>
              )}
            </div>
          </Card>

          {/* Vanity URL Section */}
          <Card 
            header={
              <div className="flex items-center">
                <div className="p-2 bg-blue-50 rounded-lg mr-4">
                  <LinkIcon className="text-blue-600 w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Personal Brand Link</h2>
                  <p className="text-sm text-gray-500 font-normal">Claim and manage your professional short link</p>
                </div>
              </div>
            }
          >
            {userData.vanityId && !isEditingVanity ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-brand-50 rounded-xl border border-brand-100 gap-4">
                <div className="flex items-center font-mono text-lg truncate max-w-full">
                  <span className="text-brand-400">reelintro.com/</span>
                  <span className="text-brand-700 font-bold">{userData.vanityId}</span>
                </div>
                <div className="flex items-center space-x-2 w-full sm:w-auto">
                  <Button variant="outline" size="sm" onClick={() => {navigator.clipboard.writeText(publicProfileUrl); toast.success('Link Copied!');}} icon={Copy}>
                    Copy
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setIsEditingVanity(true)} icon={RefreshCw}>
                    Change
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3 items-center">
                  <Input 
                    className="flex-grow w-full"
                    label="Choose your handle"
                    prefix="reelintro.com/"
                    value={vanityId}
                    onChange={(e) => setVanityId(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                    placeholder="yourname"
                    error={vanityError}
                    helperText={isEditingVanity ? "Changing this will break your old link immediately." : "Letters, numbers, dashes, and underscores only"}
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleClaimVanity}
                      disabled={!vanityAvailable || isSavingVanity}
                      loading={isSavingVanity}
                      className="whitespace-nowrap"
                    >
                      {userData.vanityId ? 'Update Link' : 'Claim Link'}
                    </Button>
                    {isEditingVanity && (
                      <Button variant="ghost" onClick={() => {setIsEditingVanity(false); setVanityId(userData.vanityId);}}>Cancel</Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card>
          
          <Card header="Social Links">
            {isEditing ? (
              <SocialLinksForm socialLinks={socialLinks} setSocialLinks={setSocialLinks} />
            ) : (
              socialLinks?.length > 0 ? (
                <SocialLinksDisplay socialLinks={socialLinks} />
              ) : (
                <p className="text-gray-500 text-sm italic">No social links added yet. Edit profile to add them.</p>
              )
            )}
          </Card>
        </div>
      ) : (
        <div ref={billingRef} className="space-y-8 animate-in slide-in-from-bottom-4 duration-500">
          <Alert 
            variant={currentTier === 'free' ? 'warning' : 'success'}
            title={`Current Plan: ${CONFIG.tiers[currentTier]?.name || 'Free Tier'}`}
            icon={ShieldCheck}
          >
            {currentTier === 'free' 
              ? "You are currently on the limited free tier. Upgrade to unlock HD quality and more videos." 
              : "Your premium features are active. Thank you for supporting ReelIntro!"}
          </Alert>

          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">Ready to level up?</h2>
            <p className="mt-4 text-xl text-gray-600">Unlock HD quality and multiple recordings to find your perfect job.</p>
          </div>

          <CardGrid cols={2}>
            <Card className="flex flex-col border-2 border-transparent">
              <div className="flex-grow">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Power User</h3>
                <div className="flex items-baseline mb-6"><span className="text-4xl font-extrabold text-gray-900">$30</span><span className="text-gray-500 ml-2">/ 6 months</span></div>
                <ul className="space-y-4">
                  <li className="flex items-start"><CheckCircle2 className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" /><span className="text-gray-600">30 Total Videos</span></li>
                  <li className="flex items-start"><CheckCircle2 className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" /><span className="text-gray-600">60s Recording Time</span></li>
                  <li className="flex items-start"><CheckCircle2 className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" /><span className="text-gray-600">Standard Quality</span></li>
                </ul>
              </div>
              <Button 
                className="mt-8 w-full"
                variant={currentTier === 'sixMonth' ? 'ghost' : 'primary'}
                onClick={() => handleUpgrade('sixMonth')} 
                disabled={currentTier === 'sixMonth' || upgradingPlan === 'sixMonth'}
                loading={upgradingPlan === 'sixMonth'}
              >
                {currentTier === 'sixMonth' ? 'Current Plan' : 'Select Plan'}
              </Button>
            </Card>

            <Card className="flex flex-col border-2 border-primary relative">
              <div className="absolute top-0 right-0 bg-primary text-white px-4 py-1 rounded-bl-lg font-bold text-xs uppercase tracking-widest">Best Value</div>
              <div className="flex-grow">
                <h3 className="text-lg font-bold text-primary mb-2">Lifetime Access</h3>
                <div className="flex items-baseline mb-6"><span className="text-4xl font-extrabold text-gray-900">$60</span><span className="text-gray-500 ml-2">one-time</span></div>
                <ul className="space-y-4">
                  <li className="flex items-start font-semibold"><CheckCircle2 className="w-5 h-5 text-primary mr-3 flex-shrink-0" /><span className="text-gray-900">150 Total Videos</span></li>
                  <li className="flex items-start font-semibold"><CheckCircle2 className="w-5 h-5 text-primary mr-3 flex-shrink-0" /><span className="text-gray-900">5 Minute Recording Time</span></li>
                  <li className="flex items-start font-semibold"><CheckCircle2 className="w-5 h-5 text-primary mr-3 flex-shrink-0" /><span className="text-gray-900">HD Video Quality</span></li>
                  <li className="flex items-start"><CheckCircle2 className="w-5 h-5 text-primary mr-3 flex-shrink-0" /><span className="text-gray-600">Personal Vanity URL</span></li>
                </ul>
              </div>
              <Button 
                className="mt-8 w-full"
                variant={currentTier === 'lifetime' ? 'ghost' : 'secondary'}
                onClick={() => handleUpgrade('lifetime')} 
                disabled={currentTier === 'lifetime' || upgradingPlan === 'lifetime'}
                loading={upgradingPlan === 'lifetime'}
              >
                {currentTier === 'lifetime' ? 'Current Plan' : 'Go Lifetime'}
              </Button>
            </Card>
          </CardGrid>

          <div className="max-w-4xl mx-auto flex items-center justify-center space-x-8 opacity-50 grayscale hover:opacity-100 transition-all">
            <div className="flex items-center text-sm font-medium text-gray-500"><Lock className="w-4 h-4 mr-2" /> Secure Stripe Checkout</div>
            <div className="flex items-center text-sm font-medium text-gray-500"><Check className="w-4 h-4 mr-2" /> Money Back Guarantee</div>
          </div>
        </div>
      )}
      
      <div className="pt-8 border-t border-gray-200">
        <Button variant="ghost" className="text-red-600 hover:text-red-800 hover:bg-red-50" onClick={handleLogout} icon={LogOut}>
          Sign Out
        </Button>
      </div>
    </div>
  );
}

export default UserProfile;
